
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import OtpInput from "react-otp-input";
import { OTP_LENGTH } from "utils/constants/others";
import { useGrnOtpVerification } from "./useGrnEmailVerification";
import ShowToast from "../Common/Toast";
import { Button, Details, WhiteWrapper } from "../Common/styles.common";
import { GrnPageWrapper, OtpContainer, OtpInputField } from "./styles.grn";
import BackButton from "../Button/BackButton";
import Spinner from "sushiweb/Spinner/Spinner";
import { strings } from "utils/constants/strings";

const VerifyEmailOtpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    email,
    source,
    purchaseOrderId,
    grnId,
    formState,
  } = location.state ?? {};

  const backPath =
    source === "all"
      ? `/grn/details/all/${purchaseOrderId}/${grnId}`
      : `/grn/details/list/${purchaseOrderId}/${grnId}`;

  // After email verification, return to the raise-dispute page so the user
  // can review their pre-filled form and explicitly submit the dispute.
  const raiseDisputePath = `/grn/dispute/${source}/${purchaseOrderId}/${grnId}`;

  const {
    otp,
    setOtp,
    loading,
    error,
    timer,
    showResend,
    handleResend,
    handleVerifyAndUpdate,
    showToast,
    setShowToast,
    verifyAndUpdateEmailErr,
  } = useGrnOtpVerification({
    email,
    // Navigate back to the raise-dispute page with the user's previously
    // entered form data restored, so they can review and submit again.
    onVerified: () =>
      navigate(raiseDisputePath, {
        state: {
          restoredDisputeDescription: formState?.disputeIssueDescription,
          restoredDisputeItemIssues: formState?.disputeItemIssues,
        },
      }),
  });

  return (
    <GrnPageWrapper className="d-flex flex-column">
      {/* Header */}
      <Details>
        <div className="d-flex align-items-center ml-4">
          <BackButton pathName={backPath} />
          <div className="d-flex flex-column ml-1 mr-4">
            <span className="fs-18 fw-700">{strings.grnVerifyEmailPageTitle}</span>
          </div>
        </div>
      </Details>

      {/* Body */}
      <div style={{ flex: 1, padding: "32px 16px 16px 16px" }}>
        <div style={{ textAlign: "center", marginBottom: "8px" }}>
          <span
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#293142",
              lineHeight: "26px",
            }}
          >
            {strings.grnVerifyEmailEnterOtp}
          </span>
        </div>
        <div style={{ textAlign: "center", marginBottom: "4px" }}>
          <span style={{ fontSize: "14px", fontWeight: 400, color: "#767C8F" }}>
            {strings.grnVerifyEmailOtpSentTo}{" "}
            <span style={{ fontWeight: 600, color: "#293142" }}>{email}</span>
          </span>
        </div>

        {/* OTP input */}
        <OtpContainer>
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={OTP_LENGTH}
            containerStyle={{ display: "flex", gap: "12px", justifyContent: "center" }}
            renderInput={(props) => (
              <OtpInputField
                {...props}
                style={{
                  borderBottomColor:
                    error ? "#EF4F5F" : otp.length > 0 ? "#293142" : "#E6E9EF",
                }}
              />
            )}
            shouldAutoFocus
          />
        </OtpContainer>

        {/* Inline error */}
        {error ? (
          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <span style={{ fontSize: "13px", color: "#EF4F5F" }}>{error}</span>
          </div>
        ) : null}

        {/* Timer / Resend */}
        <div style={{ textAlign: "center", marginTop: "16px" }}>
          {showResend ? (
            <button
              onClick={handleResend}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                fontSize: "14px",
                fontWeight: 600,
                color: "#EF4F5F",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                padding: 0,
              }}
            >
              {strings.grnVerifyEmailResendOtp}
            </button>
          ) : (
            <span style={{ fontSize: "13px", color: "#767C8F" }}>
              {strings.grnVerifyEmailResendOtpIn}{" "}
              <span style={{ fontWeight: 600, color: "#293142" }}>
                {timer}s
              </span>
            </span>
          )}
        </div>
      </div>


      <WhiteWrapper className="px-5">
        <Button
          className="fs-14 text-white fw-600 mb-3"
          onClick={handleVerifyAndUpdate}
          disabled={loading || otp.length < OTP_LENGTH}
        >
          {loading ? <Spinner color="white" /> : strings.grnVerifyEmailVerifyAndAdd}
        </Button>
      </WhiteWrapper>

      {showToast && verifyAndUpdateEmailErr && (
        <ShowToast
          errorMsg={verifyAndUpdateEmailErr}
          type="error"
          onClose={() => setShowToast(false)}
        />
      )}
    </GrnPageWrapper>
  );
};

export default VerifyEmailOtpPage;
